import type { AABB, Ring, PolygonWithHoles } from '../geo/types';
import type { Layout } from '../data/types';
import type { ChannelRegion } from './classify';
import { ensureCCW, ensureCW } from '../geo/polygon';
import { difference, intersection, union, simplify, fromRing } from '../geo/clip';

function wound(polygon: PolygonWithHoles): PolygonWithHoles {
  return { outer: ensureCCW(polygon.outer), holes: polygon.holes.map(ensureCW) };
}

// The IJ and the water areas, wound alike: under the nonzero rule an input wound the other way
// cancels the water it overlaps, which turned every channel mouth into land.
export function openWaterOf(layout: Layout): readonly PolygonWithHoles[] {
  return [layout.water.ij, ...layout.water.areas.map((area) => area.polygon)].map(wound);
}

// The dry ground in the water (layout water.dams), wound alike like the open water.
export function damsOf(layout: Layout): readonly PolygonWithHoles[] {
  return layout.water.dams.map((dam) => wound(dam.polygon));
}

// Clipped to the bounds: the far ring carries the water on from there, so nothing may reach past.
export function buildWaterUnion(
  layout: Layout,
  channels: readonly ChannelRegion[],
  bounds: AABB
): readonly PolygonWithHoles[] {
  const strips = channels.flatMap((region) => region.polygons);
  const water = difference(union([...openWaterOf(layout), ...strips], 'nonzero'), damsOf(layout));
  return simplify(intersection(water, [fromRing(boundsRing(bounds))]), 0.01);
}

export function boundsRing(bounds: AABB): Ring {
  return [
    [bounds.minX, bounds.minY],
    [bounds.maxX, bounds.minY],
    [bounds.maxX, bounds.maxY],
    [bounds.minX, bounds.maxY],
  ];
}

export { buildFarRing } from './farRing';
